import { BatteryPlus, Plus } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { TOP_UP_TILE_ID } from "./CaptainBillingTiles";
import { SWAP } from "./swap";
import { BigNumber, Tile, TileAction, type TileEdit } from "./Tile";
import { useTilePulse } from "./useTilePulse";
import { useTokenFormat } from "./useTokenFormat";
import { useTopUpTokens } from "../../api/useCaptainBillingMutations";
import { useTopUpSpent } from "../../hooks/useTopUpSpent";
import type { CaptainBillingAccount } from "../../types";
import { BalanceSwatch } from "../BalanceSwatch";
import { InlineTokensEditor, type TokensInput, type TokensSetError } from "../editors/InlineTokensEditor";
import { topUpSchema } from "../editors/tokensSchema";

import { Skeleton } from "@/components/ui/v2/skeleton";

export function TopUpTile({ account, edit }: { account: CaptainBillingAccount; edit?: TileEdit }) {
  const { t } = useTranslation();
  const formatTokens = useTokenFormat();
  const { save, isPending } = useTopUpTokens();
  const { spent: spentThisMonth, isLoading: usageLoading, isError: usageFailed } = useTopUpSpent(account);
  const pulse = useTilePulse(account.topUpBalanceTokens);

  const saveTopUp = (tokens: number, setError: TokensSetError) =>
    save<TokensInput>({ clientId: account.clientId, tokens }, setError, () => edit?.onDone());

  return (
    <Tile
      id={TOP_UP_TILE_ID}
      icon={BatteryPlus}
      label={t("captainBilling.tiles.topUp")}
      hint={t("captainBilling.tiles.hint.topUp")}
      pulse={pulse}
      centered
      corner={
        edit &&
        !edit.isEditing && <TileAction label={t("captainBilling.actions.topUp")} icon={Plus} onClick={edit.onEdit} />
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {edit?.isEditing ? (
          <motion.div key="edit" {...SWAP}>
            <InlineTokensEditor
              schema={topUpSchema(t)}
              label={t("captainBilling.editors.topUp.label")}
              defaultValue={null}
              isPending={isPending}
              onSave={saveTopUp}
              onCancel={edit.onDone}
              prefix="+"
              help={(tokens) =>
                t("captainBilling.editors.topUp.help", {
                  tokens: formatTokens(account.topUpBalanceTokens + tokens),
                })
              }
            />
          </motion.div>
        ) : (
          <motion.div key="view" {...SWAP} className="space-y-1.5">
            <BigNumber value={account.topUpBalanceTokens} qualifier={t("captainBilling.tiles.tokens")} />
            <div className="flex min-h-5 items-center gap-2 text-sm" data-testid="captain-top-up-spent">
              <BalanceSwatch source="topUp" />
              <TopUpSpent spent={spentThisMonth} isLoading={usageLoading} isError={usageFailed} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Tile>
  );
}

function TopUpSpent({ spent, isLoading, isError }: { spent: number; isLoading: boolean; isError: boolean }) {
  const { t } = useTranslation();
  const formatTokens = useTokenFormat();
  if (isLoading) return <Skeleton className="h-4 w-40" />;
  if (isError)
    return (
      <span className="text-muted-foreground" title={t("captainBilling.tiles.spentUnavailable")}>
        {t("captainBilling.tiles.topUpSpent", { tokens: "—" })}
      </span>
    );
  return (
    <span className="text-muted-foreground tabular-nums">{t("captainBilling.tiles.topUpSpent", { tokens: formatTokens(spent) })}</span>
  );
}